import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Brain,
  Smile,
  Wind,
  Repeat,
  Clock,
  Moon,
  RefreshCw,
  Sparkles,
  ChevronRight,
  ChevronDown,
  Layers,
  X,
} from 'lucide-react';
import { cn } from '../../utils';
import { MMEMParams, Persona } from '../../types';
import { PERSONAS } from '../../constants';
import SliderField from '../SliderField';

interface ControlsSidebarProps {
  params: MMEMParams;
  handleParamChange: (key: keyof MMEMParams, value: number) => void;
  loadPersona: (persona: Persona) => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

export default function ControlsSidebar({
  params,
  handleParamChange,
  loadPersona,
  isMobileOpen,
  onMobileClose,
}: ControlsSidebarProps) {
  const [openSections, setOpenSections] = useState<string[]>([
    'triad',
    'presets',
  ]);

  const toggleSection = (id: string) => {
    setOpenSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const sidebarContent = (
    <div className="space-y-8">
      {/* Parameters Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-surface border-border-main space-y-2 rounded-[2rem] border p-6 shadow-xl shadow-slate-200/40 dark:shadow-none"
      >
        {/* Encoding Triad Section */}
        <div className="overflow-hidden">
          <button
            onClick={() => toggleSection('triad')}
            className="group flex w-full items-center justify-between py-4"
          >
            <div className="flex items-center gap-2">
              <Layers className="text-secondary h-5 w-5" />
              <h2 className="text-sm font-extrabold tracking-widest text-slate-400 uppercase transition-colors group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300">
                Encoding Triad
              </h2>
            </div>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-slate-300 transition-transform duration-300',
                openSections.includes('triad') && 'rotate-180'
              )}
            />
          </button>

          <AnimatePresence initial={false}>
            {openSections.includes('triad') && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <div className="space-y-6 pt-2 pb-6">
                  <SliderField
                    label="Attention"
                    icon={<Brain className="h-4 w-4" />}
                    value={params.attention}
                    color="primary"
                    onChange={(v) => handleParamChange('attention', v)}
                  />
                  <SliderField
                    label="Mood"
                    icon={<Smile className="h-4 w-4" />}
                    value={params.mood}
                    color="secondary"
                    onChange={(v) => handleParamChange('mood', v)}
                  />
                  <SliderField
                    label="Environment"
                    icon={<Wind className="h-4 w-4" />}
                    value={params.environment}
                    color="accent"
                    onChange={(v) => handleParamChange('environment', v)}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Reinforcement Section */}
        <div className="border-border-main overflow-hidden border-t">
          <button
            onClick={() => toggleSection('reinforcement')}
            className="group flex w-full items-center justify-between py-4"
          >
            <div className="flex items-center gap-2">
              <Repeat className="text-primary h-5 w-5" />
              <h2 className="text-sm font-extrabold tracking-widest text-slate-400 uppercase transition-colors group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300">
                Reinforcement
              </h2>
            </div>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-slate-300 transition-transform duration-300',
                openSections.includes('reinforcement') && 'rotate-180'
              )}
            />
          </button>

          <AnimatePresence initial={false}>
            {openSections.includes('reinforcement') && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <div className="space-y-6 pt-2 pb-6">
                  <SliderField
                    label="Repetition"
                    icon={<RefreshCw className="h-4 w-4" />}
                    value={params.repetition}
                    max={20}
                    color="primary"
                    onChange={(v) => handleParamChange('repetition', v)}
                  />
                  <SliderField
                    label="Time Spacing"
                    icon={<Clock className="h-4 w-4" />}
                    value={params.spacing}
                    color="secondary"
                    onChange={(v) => handleParamChange('spacing', v)}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Consolidation Section */}
        <div className="border-border-main overflow-hidden border-t">
          <button
            onClick={() => toggleSection('consolidation')}
            className="group flex w-full items-center justify-between py-4"
          >
            <div className="flex items-center gap-2">
              <Moon className="h-5 w-5 text-indigo-400" />
              <h2 className="text-sm font-extrabold tracking-widest text-slate-400 uppercase transition-colors group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300">
                Consolidation
              </h2>
            </div>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-slate-300 transition-transform duration-300',
                openSections.includes('consolidation') && 'rotate-180'
              )}
            />
          </button>

          <AnimatePresence initial={false}>
            {openSections.includes('consolidation') && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <div className="pt-2 pb-6">
                  <SliderField
                    label="Sleep Quality"
                    icon={<Moon className="h-4 w-4" />}
                    value={params.sleep}
                    color="primary"
                    onChange={(v) => handleParamChange('sleep', v)}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.section>

      {/* Personas Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-surface border-border-main rounded-[2rem] border p-6 shadow-xl shadow-slate-200/40 dark:shadow-none"
      >
        <button
          onClick={() => toggleSection('presets')}
          className="group flex w-full items-center justify-between py-2"
        >
          <div className="flex items-center gap-2">
            <Sparkles className="text-primary h-5 w-5" />
            <h2 className="text-sm font-extrabold tracking-widest text-slate-400 uppercase transition-colors group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300">
              Presets
            </h2>
          </div>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-slate-300 transition-transform duration-300',
              openSections.includes('presets') && 'rotate-180'
            )}
          />
        </button>

        <AnimatePresence initial={false}>
          {openSections.includes('presets') && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <div className="space-y-3 pt-4">
                {PERSONAS.map((p) => (
                  <motion.button
                    key={p.name}
                    whileHover={{ x: 4 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => loadPersona(p)}
                    className="border-border-main hover:border-primary/20 hover:bg-primary/5 group relative w-full overflow-hidden rounded-2xl border p-5 text-left transition-all"
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-text-main group-hover:text-primary font-bold transition-colors">
                        {p.name}
                      </span>
                      <ChevronRight className="group-hover:text-primary h-4 w-4 text-slate-300 transition-colors dark:text-slate-600" />
                    </div>
                    <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                      {p.description}
                    </p>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.section>
    </div>
  );

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onMobileClose}
              className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm lg:hidden"
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="bg-surface fixed top-0 bottom-0 left-0 z-50 w-full max-w-sm overflow-y-auto p-6 lg:hidden"
            >
              <div className="flex items-center justify-between pb-4">
                <h2 className="text-lg font-bold tracking-tight">
                  Filters & Presets
                </h2>
                <button onClick={onMobileClose} className="p-1">
                  <X className="h-6 w-6" />
                </button>
              </div>
              {sidebarContent}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <div className="hidden lg:col-span-4 lg:block">{sidebarContent}</div>
    </>
  );
}
